import {
  BUSINESS_NAME,
  BASE_URL,
  BUSINESS_CONTACT_PHONE,
} from "@/core/constants/business";

interface DataItem {
  subject: string;
  city: string;
  state: string;
}

/**
 * Generates structured data for blog articles based on the content and location
 */
export const generateBlogArticleSchema = (
  dataItem: DataItem,
  title: string,
  metaDescription: string,
  datePublished?: string,
  dateModified?: string
) => {
  const today = new Date().toISOString();
  const canonicalPath = `${BASE_URL}/${dataItem.subject
    .toLowerCase()
    .replace(/\s+/g, "-")}/${dataItem.city
    .toLowerCase()
    .replace(/\s+/g, "-")}-${dataItem.state.toLowerCase()}`;

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalPath,
    },
    headline: title,
    description: metaDescription,
    image: [
      `${BASE_URL}/images/website/street-lawyer-services-weed-dispensary-dc-75.jpg`,
      `${BASE_URL}/images/street-lawyer-services-logo.png`,
    ],
    datePublished: datePublished || today,
    dateModified: dateModified || today,
    author: {
      "@type": "Organization",
      name: BUSINESS_NAME,
      url: BASE_URL,
      telephone: BUSINESS_CONTACT_PHONE,
      image: `${BASE_URL}/images/street-lawyer-services-logo.png`,
      address: {
        "@type": "PostalAddress",
        streetAddress: "3400 Connecticut Avenue NW",
        addressLocality: "Washington",
        addressRegion: "DC",
        postalCode: "20008",
        addressCountry: "US",
      },
    },
    publisher: {
      "@type": "Organization",
      name: BUSINESS_NAME,
      logo: {
        "@type": "ImageObject",
        url: `${BASE_URL}/images/street-lawyer-services-logo.png`,
      },
      telephone: BUSINESS_CONTACT_PHONE,
      image: `${BASE_URL}/images/website/street-lawyer-services-weed-dispensary-dc-75.jpg`,
      address: {
        "@type": "PostalAddress",
        streetAddress: "3400 Connecticut Avenue NW",
        addressLocality: "Washington",
        addressRegion: "DC",
        postalCode: "20008",
        addressCountry: "US",
      },
    },
    keywords: [
      dataItem.subject,
      dataItem.city,
      dataItem.state,
      "cannabis",
      "dispensary",
      "weed",
      "marijuana",
      BUSINESS_NAME,
    ],
    about: {
      "@type": "Thing",
      name: `${dataItem.subject} in ${dataItem.city}, ${dataItem.state}`,
      description: `Information about ${dataItem.subject.toLowerCase()} in ${
        dataItem.city
      }, ${dataItem.state}`,
      image: `${BASE_URL}/images/website/street-lawyer-services-weed-dispensary-dc-75.jpg`,
    },
    isPartOf: {
      "@type": "Blog",
      name: `${BUSINESS_NAME} Blog`,
      publisher: {
        "@type": "Organization",
        name: BUSINESS_NAME,
        image: `${BASE_URL}/images/street-lawyer-services-logo.png`,
        telephone: BUSINESS_CONTACT_PHONE,
        address: {
          "@type": "PostalAddress",
          streetAddress: "3400 Connecticut Avenue NW",
          addressLocality: "Washington",
          addressRegion: "DC",
          postalCode: "20008",
          addressCountry: "US",
        },
      },
    },
    locationCreated: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Washington",
        addressRegion: "DC",
        postalCode: "20008",
        streetAddress: "3400 Connecticut Avenue NW",
        addressCountry: "US",
      },
    },
    mentions: {
      "@type": "Place",
      name: dataItem.city,
      containedInPlace: {
        "@type": "State",
        name: dataItem.state === "DC" ? "District of Columbia" : dataItem.state,
      },
    },
  });
};

/**
 * Generates structured data for Cannabis Dispensary Service
 */
export const generateDispensaryServiceSchema = (
  dataItem: DataItem,
  serviceDescription: string
) => {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${dataItem.subject} in ${dataItem.city}, ${dataItem.state}`,
    serviceType: "Cannabis Products",
    provider: {
      "@type": "LocalBusiness",
      name: BUSINESS_NAME,
      telephone: BUSINESS_CONTACT_PHONE,
      image: `${BASE_URL}/images/website/street-lawyer-services-weed-dispensary-dc-75.jpg`,
      address: {
        "@type": "PostalAddress",
        streetAddress: "3400 Connecticut Avenue NW",
        addressLocality: "Washington",
        addressRegion: "DC",
        postalCode: "20008",
        addressCountry: "US",
      },
    },
    areaServed: {
      "@type": "City",
      name: dataItem.city,
      containedInPlace: {
        "@type": "State",
        name: dataItem.state === "DC" ? "District of Columbia" : dataItem.state,
      },
    },
    description: serviceDescription,
    offers: {
      "@type": "Offer",
      itemOffered: {
        "@type": "Product",
        name: `Cannabis Products for ${dataItem.city} area`,
        category: "Cannabis",
        image: `${BASE_URL}/images/website/street-lawyer-services-weed-dispensary-dc-75.jpg`,
        offers: {
          "@type": "Offer",
          price: "45.00",
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
          url: `${BASE_URL}/locations`,
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "248",
        },
      },
      areaServed: dataItem.city,
    },
  });
};

/**
 * Generates breadcrumb structured data for blog pages
 */
export const generateBreadcrumbSchema = (dataItem: DataItem) => {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${BASE_URL}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: `${BASE_URL}/blog`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: dataItem.subject,
        item: `${BASE_URL}/${dataItem.subject
          .toLowerCase()
          .replace(/\s+/g, "-")}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: `${dataItem.subject} in ${dataItem.city}, ${dataItem.state}`,
        item: `${BASE_URL}/${dataItem.subject
          .toLowerCase()
          .replace(/\s+/g, "-")}/${dataItem.city
          .toLowerCase()
          .replace(/\s+/g, "-")}-${dataItem.state.toLowerCase()}`,
      },
    ],
  });
};

/**
 * Generates all required rich snippets for a blog page
 */
export const generateBlogRichSnippets = (
  dataItem: DataItem,
  title: string,
  metaDescription: string
) => {
  return {
    articleSchema: generateBlogArticleSchema(dataItem, title, metaDescription),
    dispensaryServiceSchema: generateDispensaryServiceSchema(
      dataItem,
      metaDescription
    ),
    breadcrumbSchema: generateBreadcrumbSchema(dataItem),
  };
};
